import type { SubscriptionTier } from '../types';

/**
 * SpeedControl - Speed adjustment slider (1-15 display levels, maps to 0.05-4.0 Hz)
 *
 * Speed mapping: 1 = 0.05 Hz (very slow), 15 = 4.0 Hz (max speed)
 * Finer granularity at lower speeds (1-5) for therapeutic precision.
 */

/**
 * Convert display speed level (1-15) to Hz for internal use
 */
export function speedToHz(speed: number): number {
  // Speeds 1-5: Finer granularity at low end for EMDR therapy
  if (speed === 1) return 0.05;
  if (speed === 2) return 0.1;
  if (speed === 3) return 0.2;
  if (speed === 4) return 0.3;
  if (speed === 5) return 0.4;

  // Speeds 6-15: Linear progression from 0.5 to 4.0 Hz
  return 0.5 + (speed - 6) * (3.5 / 9);
}

interface SpeedControlProps {
  value: number;
  onChange: (speed: number) => void;
  disabled?: boolean;
  tier: SubscriptionTier;
}

export function SpeedControl({
  value,
  onChange,
  disabled = false,
}: SpeedControlProps): JSX.Element {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Speed
        </label>
        <span className="text-sm font-semibold text-gray-900">{value}</span>
      </div>

      <div className="flex items-center gap-3">
        <span className="text-lg">🐢</span>
        <input
          type="range"
          min="1"
          max="15"
          step="1"
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value))}
          disabled={disabled}
          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
        />
        <span className="text-lg">🐇</span>
      </div>
    </div>
  );
}
